<script setup lang="ts">
import { DefinitionDto } from '@/api/generator';
import Dialog from '@/components/Dialog/Dialog.vue';
import FormSection from '@/components/forms/FormSection/FormSection.vue';
import InlineValidationError from '@/components/forms/InlineValidationError/InlineValidationError.vue';
import { useFormMode } from '@/components/forms/useFormMode';
import InfoMessage from '@/components/InfoMessage/InfoMessage.vue';
import Definition from '@/views/quantities/components/useInEvaluations/Definition.vue';
import { mdiPlus } from '@mdi/js';
import { useToggle } from '@vueuse/core';
import { useFieldArray, useSetFieldTouched, useValidateField } from 'vee-validate';
import { ref, toRef } from 'vue';
import DefinitionModal from '../DefinitionModal.vue';
import { VSpacer } from 'vuetify/components';
import Button from '@/components/buttons/Button.vue';

const props = defineProps<{
  name: string;
  disabled?: boolean;
  allowExtend: boolean;
}>();

const mode = useFormMode();

const touch = useSetFieldTouched(toRef(() => props.name));
const validate = useValidateField(toRef(() => props.name));

const expandedDefinitionIndex = ref<number>();

async function touchAndValidate() {
  touch(true);
  await validate();
}

const { fields, push, remove, update } = useFieldArray<DefinitionDto>(toRef(() => props.name));

async function addDefinition(definition: DefinitionDto) {
  push(definition);

  expandedDefinitionIndex.value = fields.value.findIndex((f) => f.value.id === definition.id);

  await touchAndValidate();
  closeDialogs();
}

async function updateDefinition(definition: DefinitionDto, index: number) {
  update(index, definition);

  await touchAndValidate();
  closeDialogs();
}

async function removeDefinition(index: number) {
  remove(index);

  await touchAndValidate();
}

const [isAddDefinitionDialogOpen, toggleAddDefinitionDialog] = useToggle(false);

function closeDialogs() {
  toggleAddDefinitionDialog(false);
}
</script>

<template>
  <FormSection :disabled="!!disabled && !allowExtend">
    <v-card class="container-level-1">
      <v-card-item>
        <v-card-title class="d-flex align-center">
          <h5 class="text-h5">{{ $t('Definition_other') }}</h5>

          <InfoMessage :value="$t('PtwQuantityDefinitionsFormMsg')" :size="16" class="ml-1" />

          <VSpacer />

          <Button
            v-if="mode !== 'details'"
            variant="ghost"
            :label="$t('AddDefinition')"
            :icon="mdiPlus"
            iconPosition="start"
            size="x-small"
            :disabled="disabled && !allowExtend"
            @click="toggleAddDefinitionDialog(true)"
          />

          <Dialog v-model="isAddDefinitionDialogOpen" size="large">
            <template #title>
              {{ $t('AddDefinition') }}
            </template>

            <template #text>
              <DefinitionModal
                :value="undefined"
                :allowExtend="allowExtend"
                @update="addDefinition"
                @close="toggleAddDefinitionDialog(false)"
              />
            </template>
          </Dialog>
        </v-card-title>
      </v-card-item>
      <v-card-text>
        <InlineValidationError class="ml-2" :name="name" />

        <v-expansion-panels
          v-if="fields.length"
          v-model="expandedDefinitionIndex"
          class="list"
          data-testid="definitionPanels"
          eager
        >
          <Definition
            v-for="(definition, definitionIndex) in fields"
            :key="definition.value.name"
            :name="`${name}[${definitionIndex}]`"
            :disabled="disabled && !allowExtend"
            :allowExtend="allowExtend"
            @update="updateDefinition($event, definitionIndex)"
            @remove="removeDefinition(definitionIndex)"
            @click:edit="expandedDefinitionIndex = definitionIndex"
          />
        </v-expansion-panels>
      </v-card-text>
    </v-card>
  </FormSection>
</template>

<style scoped lang="scss">
.list {
  background: none;
  overflow: visible;

  &:deep(.v-list-item__append .v-icon__svg) {
    color: rgb(var(--v-theme-secondary-darken-2)) !important;
  }

  &:deep(.v-list-item__overlay) {
    display: none;
  }
}
</style>
